import { Button } from '@components/utility/buttons';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Card, CardProps, Divider, listItemClasses, Stack, Typography, typographyClasses } from '@mui/material';
import JsonView from '@uiw/react-json-view';
import { handleError, toaster } from '@utils/common';
import { clsx } from 'clsx';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

const CardContainer = (props: CardProps) => (
    <Card
        component="article"
        variant="outlined"
        {...props}
        className={clsx('black-border p-[28px] flex-1 flex flex-col', props.className)}
    >
        {props.children}
    </Card>
);

type PanelCardProps = CardProps & {
    header: React.ReactNode;
    body: React.ReactNode;
    cta?: React.ReactNode;
};

const PanelCard = ({ header, body, cta, ...cardProps }: PanelCardProps) => (
    <CardContainer {...cardProps}>
        <Box component="header">
            <Typography fontFamily="museo-sans" fontSize={20} letterSpacing={-1}>
                {header}
            </Typography>
        </Box>
        <Box
            className="pt-[26px] h-full"
            sx={{
                fontSize: 16,
                color: 'neutral-600.main',
                [`& .${listItemClasses.root}`]: {
                    pt: 0,
                    pb: 0.3,
                    '&::marker': {
                        color: 'neutral-600.main',
                    },
                },
                [`& .${typographyClasses.root}`]: { fontSize: 16, color: 'neutral-600.main' },
            }}
        >
            {body}
        </Box>
        {cta}
    </CardContainer>
);

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

type CodePanelCardProps = CardProps & {
    url: string;
    json: Record<string, JsonValue>;
};

const removeEllipsisEntries = (value: JsonValue): JsonValue => {
    if (Array.isArray(value)) {
        return value.map(removeEllipsisEntries);
    }

    if (value !== null && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value)
                .filter(([key, entryValue]) => key !== '...' || entryValue !== '...')
                .map(([key, entryValue]) => [key, removeEllipsisEntries(entryValue)])
        );
    }

    return value;
};

const copyJson = async (json: Record<string, JsonValue>, intl: IntlShape) => {
    try {
        await navigator.clipboard.writeText(JSON.stringify(removeEllipsisEntries(json), null, 2));
        toaster.success({
            title: '',
            msg: intl.formatMessage({ id: 'common.json_copied', defaultMessage: 'JSON copied to clipboard' }),
        });
    } catch (error) {
        handleError(
            intl.formatMessage({ id: 'common.error_copying_json', defaultMessage: 'Error copying JSON to clipboard' }),
            error
        );
    }
};

const customTheme = {
    '--w-rjv-curlybraces-color': '#0C70D4',
    '--w-rjv-key-string': '#FF2B2F',
    '--w-rjv-type-string-color': '#232527',
    '--w-rjv-type-int-color': '#0C70D4',
    '--w-rjv-quotes-color': '#FF2B2F',
    '--w-rjv-quotes-string-color': '#232527',
};

const CodePanelCard = ({ url, json, ...props }: CodePanelCardProps) => {
    const intl = useIntl();
    return (
        <CardContainer {...props} className={clsx('!p-0', props.className)}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                component="header"
                p="12px"
                bgcolor="neutral-100.main"
            >
                <Typography color="neutral-600" fontSize={16} fontStyle="italic" letterSpacing={0}>
                    {url}
                </Typography>
                <Button size="small" fullRounded startIcon={<ContentCopyIcon />} onClick={() => copyJson(json, intl)}>
                    <FormattedMessage id="common.copy" defaultMessage="Copy" />
                </Button>
            </Stack>
            <Divider />
            <Box
                py="22px"
                px="14px"
                sx={{
                    '.w-rjv-wrap': { borderLeft: 'none !important' },
                    '.w-rjv-wrap > .w-rjv-line:not(:last-child)::after': { content: '","', color: '#0C70D4' },
                    '.w-rjv-wrap > .w-rjv-inner:not(:last-child) > :last-child::after': {
                        content: '","',
                        color: '#0C70D4',
                    },
                    '.w-rjv-wrap > .w-rjv-line[data-ellipsis="true"]': {
                        color: '#0C70D4',
                        '&::after': { content: 'none' },
                    },
                }}
            >
                <JsonView
                    value={json}
                    collapsed={false}
                    style={{
                        fontFamily: 'Inter',
                        fontSize: '16px',
                        ...customTheme,
                    }}
                    displayDataTypes={false}
                    indentWidth={25}
                    enableClipboard={false}
                    displayObjectSize={false}
                >
                    <JsonView.Arrow style={{ display: 'none' }} />
                    <JsonView.Row
                        render={({ children, ...rowProps }, { keyName, parentValue }) => {
                            const isEllipsis =
                                keyName === '...' &&
                                parentValue !== undefined &&
                                Reflect.get(parentValue, keyName) === '...';

                            return (
                                <div {...rowProps} data-ellipsis={isEllipsis ? 'true' : undefined}>
                                    {isEllipsis ? '...' : children}
                                </div>
                            );
                        }}
                    />
                    {/* The following is to hide colons and index numbers for array entries. Reference: https://github.com/mac-s-g/react-json-view#custom-rendering */}
                    <JsonView.Colon
                        render={(props, { parentValue }) => {
                            if (Array.isArray(parentValue) && props.children == ':') {
                                return <span />;
                            }
                            return <span {...props} />;
                        }}
                    />
                    <JsonView.KeyName
                        render={({ children, ...rest }, { parentValue }) => {
                            if (Array.isArray(parentValue) && Number.isFinite(children)) {
                                return <span />;
                            }
                            return <span {...rest}>{children}</span>;
                        }}
                    />
                </JsonView>
            </Box>
        </CardContainer>
    );
};

const PanelCardNamespace = Object.assign(PanelCard, {
    Standard: PanelCard,
    Code: CodePanelCard,
});

export default PanelCardNamespace;
